import { useMemo, CSSProperties, ReactNode } from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  MoviefoneAnalysis,
  SearchCandidate,
} from 'gen/stream-api/requests/types.gen';
import {
  formatMaxProfit,
  formatMaxRisk,
  netCash,
  rewardToRiskRatio,
  tierColor,
  tierFromRiskScore,
} from 'util/moviefoneFormat';

import range from 'lodash/range';
import { MoviefoneDetailView } from 'types/moviefone';
import { LegsSummary, TradeCardBody } from './MoviefoneShared';
import { getLastMarketClose, dayjs } from 'util/shared/date';

const TABLE_TO_INLINE_LABELS_BREAK_EX = 60;

const TRADE_COLUMNS: {
  label: string;
  accessor: (s: SearchCandidate) => string;
  detail?: (s: SearchCandidate) => ReactNode;
  cellBoxSx?: (s: SearchCandidate) => Partial<CSSProperties>;
}[] = [
  {
    label: 'Structure',
    accessor: (s) => s.family_label!,
    detail: (s) => <LegsSummary legs={s.legs} variant="caption" />,
    cellBoxSx: (_s) => ({
      textAlign: 'left',
    }),
  },
  {
    label: 'Net Debit/Credit',
    accessor: (s) => {
      const cash = netCash(s);
      return `${cash.value} ${cash.label === 'Net Debit' ? 'debit' : 'credit'}`;
    },
    cellBoxSx: (_s) => ({
      textAlign: 'right',
      whiteSpace: 'nowrap',
    }),
  },
  {
    label: 'Max Profit',
    accessor: (s) => formatMaxProfit(s),
    cellBoxSx: (_s) => ({
      textAlign: 'right',
    }),
  },
  {
    label: 'Max Loss',
    accessor: (s) => formatMaxRisk(s),
    cellBoxSx: (_s) => ({
      textAlign: 'right',
    }),
  },
  {
    label: 'Probability of Profit',
    accessor: (s) =>
      s.final_score != null && s.pop != null
        ? new Intl.NumberFormat('en', { maximumSignificantDigits: 2 }).format(
            s.pop,
          ) + '%'
        : '',
  },
  {
    label: 'Risk-Reward Ratio',
    accessor: (s) => {
      const ratio = rewardToRiskRatio(s);
      return ratio != null
        ? new Intl.NumberFormat('en', { maximumSignificantDigits: 2 }).format(
            ratio,
          )
        : 'N/A';
    },
    cellBoxSx: (_s) => ({
      textAlign: 'center',
    }),
  },
];

const CARD_WIDTH = 300;
const CARD_GAP = 12;
const ROW_PADDING = 40;
const rowWidth = (count: number) =>
  count * CARD_WIDTH + (count - 1) * CARD_GAP + ROW_PADDING;

interface MoviefoneNTradesProps {
  moviefoneAnalysis?: MoviefoneAnalysis | null;
  /** From selectTradeStructures. */
  selectedTradeStructures: SearchCandidate[];
  /** Highlighted when it shows one of these trades, or the compare view of them. */
  detailView?: MoviefoneDetailView | null;
  onSelectTrade?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    searchCandidate: SearchCandidate,
  ) => void;
  onSelectCompareMode?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    strategies: SearchCandidate[],
  ) => void;
}

const MoviefoneNTrades = ({
  moviefoneAnalysis,
  selectedTradeStructures,
  detailView,
  onSelectTrade,
  onSelectCompareMode,
}: MoviefoneNTradesProps) => {
  const theme = useTheme();

  const cards = useMemo(
    () =>
      selectedTradeStructures.map((searchCandidate) => ({
        // same tiers pickTradeCards assigns
        tier:
          searchCandidate.risk_score == null
            ? null
            : tierFromRiskScore(searchCandidate.risk_score.total),
        searchCandidate,
      })),
    [selectedTradeStructures],
  );

  if (moviefoneAnalysis?.structure_search?.candidates == null) {
    return null;
  }

  // Not one of `cards` - a trailing sentinel index for the Compare Options tile.
  const compareIndex = cards.length;
  const selectCompareMode = () => {
    onSelectCompareMode?.(moviefoneAnalysis, selectedTradeStructures);
  };
  // By identity, so only the message whose trades the detail view came from highlights.
  const selectedIndex =
    detailView?.kind === 'SearchCandidate'
      ? selectedTradeStructures.indexOf(detailView.searchCandidate)
      : detailView?.kind === 'compare' &&
          detailView.strategies === selectedTradeStructures
        ? compareIndex
        : null;

  return (
    <Box sx={{ padding: '0 0 16px', containerType: 'inline-size' }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: theme.palette.text.primary,
          textAlign: 'center',
        }}
      >
        {moviefoneAnalysis.symbol} Trades
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 600,
          fontStyle: 'italic',
          color: theme.palette.text.primary,
          textAlign: 'center',
        }}
      >
        {cards.length == 0
          ? 'No trades matching your criteria were found. Please try again.'
          : `Analysis generated from data near market close ${getLastMarketClose(dayjs(moviefoneAnalysis.created_at ?? moviefoneAnalysis.trade_date)).format('YYYY-MM-DD')}`}
      </Typography>
      {cards.length > 0 && (
        <>
          <Box sx={{ marginBottom: '10ex' }}>
            <Box
              sx={{
                [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    'grid-template-columns': range(TRADE_COLUMNS.length)
                      .map((idx) => (idx === 0 ? '2.5fr' : '1fr'))
                      .join(' '),
                    display: 'grid',
                    rowGap: '5px',

                    // Each data row is a run of TRADE_COLUMNS.length flat grid
                    // siblings (no wrapper element, so column alignment across rows
                    // is untouched). These classes stitch that run into the look of
                    // a single bordered row, colored per-cell via the `--row-color`
                    // custom property.
                    '& .trade-row-cell': {
                      borderTop: '1px solid var(--row-color)',
                      borderBottom: '1px solid var(--row-color)',
                    },
                    '& .trade-row-cell--first': {
                      borderLeft: '1px solid var(--row-color)',
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                    },
                    '& .trade-row-cell--last': {
                      borderRight: '1px solid var(--row-color)',
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    },
                  },
                [`@container (max-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    display: 'none',
                  },

                padding: '20px',
                margin: '0 auto',
                maxWidth: `${TABLE_TO_INLINE_LABELS_BREAK_EX * 1.6}ex`,
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {TRADE_COLUMNS.map((c) => (
                <Box
                  sx={{
                    textAlign: 'center',
                  }}
                  key={c.label}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                  >
                    {c.label.toUpperCase()}
                  </Typography>
                  {cards.length == 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                    >
                      No trades matching your criteria were found.
                    </Typography>
                  )}
                </Box>
              ))}
              {cards.map(({ tier, searchCandidate: s }, index) =>
                TRADE_COLUMNS.map((c, idx) => (
                  <Box
                    key={`${index}_${idx}`}
                    className={[
                      'trade-row-cell',
                      idx === 0 && 'trade-row-cell--first',
                      idx === TRADE_COLUMNS.length - 1 &&
                        'trade-row-cell--last',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      onSelectTrade?.(moviefoneAnalysis, s);
                    }}
                    style={
                      {
                        '--row-color': tierColor(tier, theme),
                      } as CSSProperties
                    }
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        index == selectedIndex
                          ? theme.palette.mutedSgGreen
                          : theme.palette.background.paper,
                      padding: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex )`]:
                          {
                            display: 'none',
                          },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                      >
                        {c.label.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        textAlign: 'center',
                        ...(c.cellBoxSx?.(s) ?? {}),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        {c.accessor(s)}
                      </Typography>
                      {c.detail?.(s)}
                    </Box>
                  </Box>
                )),
              )}

              <Box
                onClick={selectCompareMode}
                sx={{
                  gridColumn: '1 / -1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '4px',
                  padding: '10px',
                  borderRadius: '4px',
                  border: `1px dashed ${alpha(theme.palette.text.secondary, 0.5)}`,
                  backgroundColor:
                    selectedIndex === compareIndex
                      ? theme.palette.mutedSgGreen
                      : theme.palette.background.paper,
                }}
              >
                <CompareArrowsIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px' }}
                >
                  Compare Options
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    display: 'none',
                  },
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: `${CARD_GAP}px`,
                padding: '20px',
                margin: '0 auto',
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper,
                // width is capped to the container's available space once
                // flex-wrap kicks in, so `fit-content` can't shrink to the
                // width of the row that actually rendered. Set it explicitly
                // per breakpoint, capped at the number of cards we actually have.
                width: `${rowWidth(1)}px`,
                ...(cards.length + 1 >= 2 && {
                  '@container (min-width: 652px)': {
                    width: `${rowWidth(2)}px`,
                  },
                }),
                ...(cards.length + 1 >= 3 && {
                  '@container (min-width: 964px)': {
                    width: `${rowWidth(3)}px`,
                  },
                }),
                ...(cards.length + 1 >= 4 && {
                  '@container (min-width: 1276px)': {
                    width: `${rowWidth(4)}px`,
                  },
                }),
              }}
            >
              {cards.map(({ tier, searchCandidate: s }, index) => {
                const isSelected = index === selectedIndex;
                const color = tierColor(tier, theme);
                return (
                  <Box
                    key={`${tier ?? 'untiered'}-${s.family_label}-${index}`}
                    onClick={() => {
                      onSelectTrade?.(moviefoneAnalysis, s);
                    }}
                    sx={{
                      width: `${CARD_WIDTH}px`,
                      padding: '16px',
                      borderRadius: '8px',
                      border: `1px solid ${color}`,
                      backgroundColor: isSelected
                        ? theme.palette.mutedSgGreen
                        : theme.palette.background.paper,
                      cursor: 'pointer',
                    }}
                  >
                    <TradeCardBody searchCandidate={s} />
                  </Box>
                );
              })}

              <Box
                onClick={selectCompareMode}
                sx={{
                  width: `${CARD_WIDTH}px`,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px dashed ${alpha(theme.palette.text.secondary, 0.5)}`,
                  backgroundColor:
                    selectedIndex === compareIndex
                      ? theme.palette.mutedSgGreen
                      : theme.palette.background.paper,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <CompareArrowsIcon
                  sx={{ color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  Compare Options
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MoviefoneNTrades;
